import { useState, useEffect, useMemo } from "react";
import { type AdminUserSummary, type Paginated } from "@currencycore/shared";
import { listUsers } from "../../lib/api.ts";
import {
  renderTemplate,
  type EmailTemplate,
  type TemplateVariables,
} from "../../lib/email-templates.ts";
import {
  sendOutreachEmail,
  SENDER_OPTIONS,
  DEFAULT_SENDER_EMAIL,
} from "../../lib/email-service.ts";
import { PlanBadge } from "../Badges.tsx";
import { toast } from "../Toast.tsx";
import { useConfirm } from "../useConfirm.tsx";
import { fmtDate, errMsg, useDebounced } from "../../lib/ui.ts";

interface SendEmailSectionProps {
  templates: EmailTemplate[];
  selectedTemplateId?: string;
  initialUserId?: string;
  onNavigateToHistory: () => void;
}

type UserFilterPreset = "all" | "inactive" | "free" | "unverified";

export function SendEmailSection({
  templates,
  selectedTemplateId,
  initialUserId,
  onNavigateToHistory,
}: SendEmailSectionProps) {
  // 1. Sender & Template state
  const [senderEmail, setSenderEmail] = useState<string>(DEFAULT_SENDER_EMAIL);
  const [templateId, setTemplateId] = useState<string>(
    selectedTemplateId || templates[0]?.id || "",
  );

  // Update if prop changes
  useEffect(() => {
    if (selectedTemplateId) setTemplateId(selectedTemplateId);
  }, [selectedTemplateId]);

  const activeTemplate = useMemo(() => {
    return templates.find((t) => t.id === templateId) ?? templates[0];
  }, [templates, templateId]);

  // 2. Users state
  const [userData, setUserData] = useState<Paginated<AdminUserSummary> | null>(null);
  const [loadingUsers, setLoadingUsers] = useState<boolean>(true);
  const [search, setSearch] = useState<string>("");
  const debSearch = useDebounced(search);
  const [filterPreset, setFilterPreset] = useState<UserFilterPreset>("all");
  const [selectedUsers, setSelectedUsers] = useState<Map<string, { email: string; name: string | null; topPlan?: string | null; orgCount?: number; createdAt?: string | null }>>(
    new Map(),
  );
  const [customEmailInput, setCustomEmailInput] = useState<string>("");
  const [previewUserId, setPreviewUserId] = useState<string>("");
  const [sending, setSending] = useState<boolean>(false);
  const { ask, dialog } = useConfirm();

  // Load users from API/mock
  useEffect(() => {
    let alive = true;
    setLoadingUsers(true);

    listUsers({ q: debSearch.trim() || undefined, page: 1, pageSize: 50, sort: "createdAt", order: "desc" })
      .then((res) => {
        if (!alive) return;
        setUserData(res);

        // Pre-select initialUserId if provided
        if (initialUserId && !selectedUsers.has(initialUserId)) {
          const target = res.items.find((u) => u.id === initialUserId);
          if (target) {
            setSelectedUsers(
              (prev) =>
                new Map(
                  prev.set(target.id, {
                    email: target.email,
                    name: target.name,
                    topPlan: target.topPlan,
                    orgCount: target.orgCount,
                    createdAt: target.createdAt,
                  }),
                ),
            );
            setPreviewUserId(target.id);
          }
        }
      })
      .catch((err) => {
        if (alive) toast(errMsg(err, "Failed to load users."));
      })
      .finally(() => {
        if (alive) setLoadingUsers(false);
      });

    return () => {
      alive = false;
    };
  }, [debSearch, initialUserId]);

  // Filter users based on preset
  const filteredUsers = useMemo(() => {
    if (!userData) return [];
    return userData.items.filter((u) => {
      if (filterPreset === "inactive") return u.orgCount === 0;
      if (filterPreset === "free") return !u.topPlan || u.topPlan === "free";
      if (filterPreset === "unverified") return !u.emailVerified;
      return true;
    });
  }, [userData, filterPreset]);

  // Selected users as array
  const selectedList = useMemo(() => {
    return Array.from(selectedUsers.entries()).map(([id, info]) => ({
      id,
      ...info,
    }));
  }, [selectedUsers]);

  // Determine active preview variables based on selected user or fallback
  const previewVars: TemplateVariables = useMemo(() => {
    const target =
      selectedList.find((u) => u.id === previewUserId) ??
      selectedList[0] ?? {
        name: "Alex",
        email: "alex@example.com",
        topPlan: "free",
        orgCount: 0,
        createdAt: new Date().toISOString(),
      };

    return {
      name: target.name,
      email: target.email,
      top_plan: target.topPlan ?? "free",
      org_count: target.orgCount ?? 0,
      signup_date: target.createdAt ? target.createdAt.slice(0, 10) : "recently",
    };
  }, [selectedList, previewUserId]);

  // Rendered template preview
  const rendered = useMemo(() => {
    if (!activeTemplate) return { subject: "", html: "", text: "" };
    return renderTemplate(activeTemplate, previewVars);
  }, [activeTemplate, previewVars]);

  function toggleUser(u: AdminUserSummary) {
    setSelectedUsers((prev) => {
      const next = new Map(prev);
      if (next.has(u.id)) {
        next.delete(u.id);
      } else {
        next.set(u.id, {
          email: u.email,
          name: u.name,
          topPlan: u.topPlan,
          orgCount: u.orgCount,
          createdAt: u.createdAt,
        });
      }
      return next;
    });
  }

  function selectAllCurrent() {
    setSelectedUsers((prev) => {
      const next = new Map(prev);
      filteredUsers.forEach((u) => {
        next.set(u.id, {
          email: u.email,
          name: u.name,
          topPlan: u.topPlan,
          orgCount: u.orgCount,
          createdAt: u.createdAt,
        });
      });
      return next;
    });
  }

  function clearAllSelected() {
    setSelectedUsers(new Map());
  }

  function handleAddCustomEmail(e: React.FormEvent) {
    e.preventDefault();
    const email = customEmailInput.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast("Please enter a valid email address.");
      return;
    }
    const customId = `custom_${email}`;
    setSelectedUsers((prev) => {
      const next = new Map(prev);
      next.set(customId, {
        email,
        name: email.split("@")[0] ?? "there",
        topPlan: "free",
        orgCount: 0,
        createdAt: new Date().toISOString(),
      });
      return next;
    });
    setCustomEmailInput("");
    toast(`Added ${email} to recipients.`);
  }

  function handleSend() {
    if (!selectedList.length) {
      toast("Please select at least one recipient.");
      return;
    }
    if (!activeTemplate) {
      toast("Please select an email template.");
      return;
    }

    ask({
      title: `Send email to ${selectedList.length} user${selectedList.length > 1 ? "s" : ""}?`,
      message: (
        <div>
          <p style={{ marginBottom: "12px" }}>
            You are about to dispatch <strong>"{activeTemplate.name}"</strong> to:
          </p>
          <div
            style={{
              maxHeight: "140px",
              overflowY: "auto",
              padding: "8px 12px",
              background: "var(--surface)",
              borderRadius: "8px",
              border: "1px solid var(--border)",
              fontSize: "13px",
              marginBottom: "12px",
            }}
          >
            {selectedList.map((u) => (
              <div key={u.id} className="mono" style={{ padding: "2px 0" }}>
                &bull; {u.name ? `${u.name} <${u.email}>` : u.email}
              </div>
            ))}
          </div>
          <p className="hint">
            Emails will be sent from <strong>CurrencyCore &lt;{senderEmail}&gt;</strong>.
            Any replies will automatically route directly to your inbox.
          </p>
        </div>
      ),
      confirmLabel: `Send ${selectedList.length} email${selectedList.length > 1 ? "s" : ""}`,
      run: async () => {
        setSending(true);
        try {
          // Pre-render personalized email content for each recipient
          const recipientsWithPersonalization = selectedList.map((u) => {
            const userRendered = renderTemplate(activeTemplate, {
              name: u.name,
              email: u.email,
              top_plan: u.topPlan ?? "free",
              org_count: u.orgCount ?? 0,
              signup_date: u.createdAt,
            });
            return {
              email: u.email,
              name: u.name,
              subject: userRendered.subject,
              html: userRendered.html,
              text: userRendered.text,
            };
          });

          const result = await sendOutreachEmail({
            fromEmail: senderEmail,
            recipients: recipientsWithPersonalization,
            subject: activeTemplate.subject,
            html: activeTemplate.html,
            text: activeTemplate.text,
            templateId: activeTemplate.id,
            templateName: activeTemplate.name,
          });

          if (result.ok && result.sentCount > 0) {
            toast(`Successfully sent email to ${result.sentCount} recipient${result.sentCount > 1 ? "s" : ""}!`, true);
            clearAllSelected();
            onNavigateToHistory();
          } else {
            const firstErr = result.errors?.[0]?.error;
            toast(`Failed to send email${firstErr ? `: ${firstErr}` : " to selected recipients."}`);
          }
        } catch (err) {
          toast(errMsg(err, "Failed to send emails."));
        } finally {
          setSending(false);
        }
      },
    });
  }

  const allFilteredSelected =
    filteredUsers.length > 0 && filteredUsers.every((u) => selectedUsers.has(u.id));

  return (
    <div className="email-send-layout">
      {/* Left Column: Configuration & Recipients */}
      <div className="email-send-config-pane">
        {/* Step 1: Template & Sender Picker */}
        <div className="card card-pad mb-4">
          <div className="section-head">
            <h3>1. Template &amp; Sender</h3>
            {activeTemplate?.isDefault ? (
              <span className="badge badge-neutral">Default</span>
            ) : (
              <span className="badge badge-accent">My Template</span>
            )}
          </div>

          <div className="form-group mt-3">
            <label className="label">Sender Email Address</label>
            <select
              className="select"
              value={senderEmail}
              onChange={(e) => setSenderEmail(e.target.value)}
            >
              {SENDER_OPTIONS.map((opt) => (
                <option key={opt.email} value={opt.email}>
                  {opt.label}
                </option>
              ))}
            </select>
            <span className="hint">
              Replies from users will route directly to this address.
            </span>
          </div>

          <div className="form-group mt-3">
            <label className="label">Email Template</label>
            <select
              className="select"
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
            >
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} ({t.isDefault ? "Default" : "Custom"})
                </option>
              ))}
            </select>
          </div>

          {activeTemplate && (
            <div className="hint mt-2">
              <strong>Subject:</strong> {activeTemplate.subject}
            </div>
          )}
        </div>

        {/* Step 2: Recipients Selection */}
        <div className="card card-pad">
          <div className="section-head">
            <div>
              <h3>2. Select Users</h3>
              <p className="hint">
                Target inactive users, recent signups, or developers needing support.
              </p>
            </div>
            <span className="badge badge-accent tnum">
              {selectedList.length} selected
            </span>
          </div>

          {/* Quick preset filters */}
          <div className="email-filter-presets mt-3">
            <button
              type="button"
              className={`pill-btn ${filterPreset === "all" ? "active" : ""}`}
              onClick={() => setFilterPreset("all")}
            >
              All Users
            </button>
            <button
              type="button"
              className={`pill-btn ${filterPreset === "inactive" ? "active" : ""}`}
              onClick={() => setFilterPreset("inactive")}
              title="Users with 0 organizations (signed up, not active)"
            >
              Inactive (0 Orgs)
            </button>
            <button
              type="button"
              className={`pill-btn ${filterPreset === "free" ? "active" : ""}`}
              onClick={() => setFilterPreset("free")}
            >
              Free Plan
            </button>
            <button
              type="button"
              className={`pill-btn ${filterPreset === "unverified" ? "active" : ""}`}
              onClick={() => setFilterPreset("unverified")}
            >
              Unverified
            </button>
          </div>

          {/* Search bar & Add manual recipient */}
          <div className="recipient-search-row mt-3">
            <input
              type="text"
              className="input input-sm"
              placeholder="Filter by name or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <form onSubmit={handleAddCustomEmail} className="custom-email-row mt-2">
            <input
              type="email"
              className="input input-sm"
              placeholder="Or add one-off email address…"
              value={customEmailInput}
              onChange={(e) => setCustomEmailInput(e.target.value)}
            />
            <button type="submit" className="btn btn-secondary btn-sm">
              + Add
            </button>
          </form>

          {/* Selected recipients chips */}
          {selectedList.length > 0 && (
            <div className="selected-chips-box mt-3">
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs muted">Selected Recipients:</span>
                <button
                  type="button"
                  className="btn btn-ghost btn-xs"
                  onClick={clearAllSelected}
                >
                  Clear all
                </button>
              </div>
              <div className="selected-chips-wrap">
                {selectedList.map((u) => (
                  <span key={u.id} className="recipient-chip">
                    <span>{u.email}</span>
                    <button
                      type="button"
                      className="chip-remove-btn"
                      onClick={() =>
                        setSelectedUsers((prev) => {
                          const next = new Map(prev);
                          next.delete(u.id);
                          return next;
                        })
                      }
                      title="Remove recipient"
                    >
                      &times;
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Users Table */}
          <div className="recipient-table-wrap mt-3">
            <table className="data-table data-table-sm">
              <thead>
                <tr>
                  <th style={{ width: "36px" }}>
                    <input
                      type="checkbox"
                      checked={allFilteredSelected}
                      onChange={() => {
                        if (allFilteredSelected) {
                          setSelectedUsers((prev) => {
                            const next = new Map(prev);
                            filteredUsers.forEach((u) => next.delete(u.id));
                            return next;
                          });
                        } else {
                          selectAllCurrent();
                        }
                      }}
                      title="Select all on this list"
                    />
                  </th>
                  <th>User</th>
                  <th>Plan</th>
                  <th className="col-num">Orgs</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {loadingUsers && !userData ? (
                  <tr>
                    <td colSpan={5} className="table-loading">
                      <span className="spinner" /> Loading users…
                    </td>
                  </tr>
                ) : filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="table-empty">
                      No users match the criteria.
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((u) => {
                    const isChecked = selectedUsers.has(u.id);
                    return (
                      <tr
                        key={u.id}
                        className={`clickable-row ${isChecked ? "row-selected" : ""}`}
                        onClick={() => toggleUser(u)}
                      >
                        <td onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleUser(u)}
                          />
                        </td>
                        <td>
                          <div className="cell-title">{u.name ?? "-"}</div>
                          <div className="cell-sub mono muted">{u.email}</div>
                        </td>
                        <td>
                          {u.topPlan ? (
                            <PlanBadge plan={u.topPlan} />
                          ) : (
                            <span className="badge badge-neutral">Free</span>
                          )}
                        </td>
                        <td className="col-num tnum">{u.orgCount}</td>
                        <td className="muted tnum">{fmtDate(u.createdAt)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Right Column: Live Rendered Dynamic Preview & Dispatch */}
      <div className="email-send-preview-pane">
        <div className="card card-pad">
          <div className="preview-topbar">
            <div>
              <h3>3. Preview &amp; Confirm</h3>
              <p className="hint">Review real variable substitution before sending.</p>
            </div>

            {selectedList.length > 0 && (
              <div className="preview-user-switcher">
                <span className="text-xs muted">Previewing as:</span>
                <select
                  className="select select-sm"
                  value={previewUserId || selectedList[0]?.id}
                  onChange={(e) => setPreviewUserId(e.target.value)}
                >
                  {selectedList.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name ? `${u.name} (${u.email})` : u.email}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="email-meta-strip mt-3">
            <div className="email-meta-item">
              <span className="email-meta-label">From:</span>
              <span className="email-meta-value mono">CurrencyCore &lt;{senderEmail}&gt;</span>
            </div>
            <div className="email-meta-item">
              <span className="email-meta-label">Reply-To:</span>
              <span className="email-meta-value mono">{senderEmail} (routes to your inbox)</span>
            </div>
            <div className="email-meta-item">
              <span className="email-meta-label">Subject:</span>
              <span className="email-meta-value mono">{rendered.subject}</span>
            </div>
          </div>

          <div className="email-canvas-wrapper mt-3" style={{ minHeight: "420px" }}>
            <div className="email-iframe-box desktop">
              <iframe
                title="Live Rendered Email Preview"
                srcDoc={rendered.html}
                sandbox="allow-popups allow-same-origin"
                className="email-iframe"
              />
            </div>
          </div>

          <div className="send-action-bar mt-4">
            <div className="send-summary-text">
              {selectedList.length === 0 ? (
                <span className="muted">No recipients selected yet.</span>
              ) : (
                <span>
                  Ready to send to <strong>{selectedList.length}</strong> recipient
                  {selectedList.length > 1 ? "s" : ""}.
                </span>
              )}
            </div>

            <button
              type="button"
              className="btn btn-primary btn-lg"
              disabled={selectedList.length === 0 || sending}
              onClick={handleSend}
            >
              {sending ? (
                <>
                  <span className="spinner" /> Sending…
                </>
              ) : (
                <>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M22 2L11 13" />
                    <polygon points="22 2 15 22 11 13 2 9 22 2" />
                  </svg>
                  Send Email to {selectedList.length || 0} User
                  {selectedList.length === 1 ? "" : "s"}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {dialog}
    </div>
  );
}
